import React, { FC } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { MAX_EDUCATION_ROWS } from "../../PaygapAnalysisParameters";
import type { AnalysisParameters, EducationTrainingItem } from "../../../../hooks";

import { Button, TextField } from "@components/base";

interface EducationRowsFormProps {
  educationAndTraining: EducationTrainingItem[];
  parameters: AnalysisParameters;
  onEducationAndTrainingChange: (
    index: number,
    field: keyof EducationTrainingItem,
    value: string | number,
  ) => void;
  onAddEducationAndTraining: () => void;
  onRemoveEducationAndTraining: (index: number) => void;
}

export const EducationRowsForm: FC<EducationRowsFormProps> = ({
  educationAndTraining,
  parameters,
  onEducationAndTrainingChange,
  onAddEducationAndTraining,
  onRemoveEducationAndTraining,
}) => {
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {educationAndTraining.map((item, index) => (
        <Box
          key={`education-row-${index}`}
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "120px 1fr 1fr 64px" }}
          gap={1}
          alignItems="center"
        >
          <TextField
            label={t("paygap.referentials.id")}
            type="number"
            value={item.id}
            error={
              !!item.id.trim() &&
              parameters.educationAndTraining.filter(
                (row) => row.id.trim() === item.id.trim(),
              ).length > 1
            }
            helperText={
              !!item.id.trim() &&
              parameters.educationAndTraining.filter(
                (row) => row.id.trim() === item.id.trim(),
              ).length > 1
                ? t("paygap.referentials.uniqueId")
                : undefined
            }
            onChange={(e) =>
              onEducationAndTrainingChange(index, "id", e.target.value)
            }
            fullWidth
          />
          <TextField
            label={t("paygap.referentials.years")}
            type="number"
            value={item.numberOfYears}
            onChange={(e) =>
              onEducationAndTrainingChange(index, "numberOfYears", e.target.value)
            }
            fullWidth
          />
          <TextField
            label={t("paygap.referentials.name")}
            value={item.name}
            onChange={(e) =>
              onEducationAndTrainingChange(index, "name", e.target.value)
            }
            fullWidth
          />
          <IconButton
            aria-label={t("paygap.referentials.remove")}
            onClick={() => onRemoveEducationAndTraining(index)}
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Box display="flex" alignItems="center" gap={2}>
        <Button
          variant="outlined"
          onClick={onAddEducationAndTraining}
          disabled={educationAndTraining.length >= MAX_EDUCATION_ROWS}
        >
          {t("paygap.referentials.addRow")}
        </Button>
        <Typography variant="caption" color="text.secondary">
          {`${educationAndTraining.length}/${MAX_EDUCATION_ROWS}`} ·{" "}
          {t("paygap.referentials.educationLimit")}
        </Typography>
      </Box>
    </Box>
  );
};
